import * as React from 'react';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, NumericTextBox } from '@progress/kendo-react-inputs';
import { Checkbox } from '@progress/kendo-react-inputs';
import { fetchApi } from '../../services/api';
import { Title } from '../../utils/title';
import { arrowRotateCwIcon, plusIcon } from '@progress/kendo-svg-icons';
import DateCell from 'components/cells/DateCell';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label } from '@progress/kendo-react-labels';

interface UserTeamViewModel {
  UserTeamID: number;
  Name: string;
  Active: boolean;
  WeeklyBookedCount: number | null;
  WeeklyTotalCharges: number | null;
  WeeklyTotalMargin: number | null;
  CreatedDate: string;
  ModifiedDateTime: string;
  RetiredDate: string | null;
}

interface SaveUserTeamCommand {
  UserTeamID?: number;
  Name: string;
  Active: boolean;
  WeeklyBookedCount: number | null;
  WeeklyTotalCharges: number | null;
  WeeklyTotalMargin: number | null;
}

interface State {
  loading: boolean;
  userTeams: UserTeamViewModel[];
  showDialog: boolean;
  editingTeam: SaveUserTeamCommand;
  saving: boolean;
}

export default class UserTeams extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);

    this.state = {
      loading: true,
      userTeams: [],
      showDialog: false,
      editingTeam: { Name: '', Active: true, WeeklyBookedCount: null, WeeklyTotalCharges: null, WeeklyTotalMargin: null },
      saving: false
    };

    this.loadUserTeams = this.loadUserTeams.bind(this);
    this.openDialog = this.openDialog.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.saveTeam = this.saveTeam.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  public componentDidMount() {
    this.loadUserTeams();
  }

  private async loadUserTeams() {
    this.setState({ loading: true });
    try {
      const userTeams = await fetchApi('/api/UserTeam/List');
      this.setState({ userTeams, loading: false });
    } catch (error) {
      console.error('Error loading user teams:', error);
      this.setState({ loading: false });
    }
  }

  private openDialog(team?: UserTeamViewModel) {
    if (team) {
      this.setState({
        showDialog: true,
        editingTeam: {
          UserTeamID: team.UserTeamID,
          Name: team.Name,
          Active: team.Active,
          WeeklyBookedCount: team.WeeklyBookedCount,
          WeeklyTotalCharges: team.WeeklyTotalCharges,
          WeeklyTotalMargin: team.WeeklyTotalMargin
        }
      });
    } else {
      this.setState({
        showDialog: true,
        editingTeam: { Name: '', Active: true, WeeklyBookedCount: null, WeeklyTotalCharges: null, WeeklyTotalMargin: null }
      });
    }
  }

  private closeDialog() {
    this.setState({
      showDialog: false,
      editingTeam: { Name: '', Active: true, WeeklyBookedCount: null, WeeklyTotalCharges: null, WeeklyTotalMargin: null }
    });
  }

  private async saveTeam() {
    if (!this.state.editingTeam.Name.trim()) {
      alert('Team name is required');
      return;
    }

    this.setState({ saving: true });
    try {
      await fetchApi('/api/UserTeam/Save', this.state.editingTeam, 'POST');
      await this.loadUserTeams();
      this.closeDialog();
    } catch (error) {
      console.error('Error saving team:', error);
      alert('Error saving team');
    } finally {
      this.setState({ saving: false });
    }
  }

  private handleInputChange(field: keyof SaveUserTeamCommand, value: string | boolean | number | null) {
    this.setState({
      editingTeam: {
        ...this.state.editingTeam,
        [field]: value
      }
    });
  }

  public render() {
    const { loading, userTeams, showDialog, editingTeam, saving } = this.state;

    return <>
      <Title string="User Team Management" />
      <br />
      <Grid
        data={userTeams}
        showLoader={loading}
        scrollable='none'
      >
        <GridToolbar>
          <Button
            themeColor="primary"
            onClick={() => this.openDialog()}
            disabled={loading}
            svgIcon={plusIcon}
          >
            Add New Team
          </Button>
          <Button
            themeColor="secondary"
            onClick={() => this.loadUserTeams()}
            svgIcon={arrowRotateCwIcon}
            disabled={loading}
          />
        </GridToolbar>
        <Column 
          field="Name" 
          title="Team Name"
        />
        <Column 
          field="Active" 
          title="Active"
          cells={{ data: (props) => (
            <td {...props.tdProps}>
              <span className={`badge ${props.dataItem.Active ? 'badge-success' : 'badge-secondary'}`}>
                {props.dataItem.Active ? 'Active' : 'Retired'}
              </span>
            </td>
          )}}
        />
        <Column 
          field="WeeklyBookedCount"
          title="Weekly Booked Count"
          format="{0:n0}"
        />
        <Column 
          field="WeeklyTotalCharges"
          title="Weekly Total Charges"
          format="{0:c}"
        />
        <Column 
          field="WeeklyTotalMargin"
          title="Weekly Total Margin"
          format="{0:c}"
        />
        <Column 
          field="CreatedDate"
          title="Created"
          cells={{ data: DateCell }}
        />
        <Column 
          field="ModifiedDateTime"
          title="Modified"
          cells={{ data: DateCell }}
        />
        <Column 
          field="RetiredDate"
          title="Retired"
          cells={{ data: DateCell }}
        />
        <Column
          cells={{ data: (props) => (
            <td {...props.tdProps}>
              <Button
                size="small"
                onClick={() => this.openDialog(props.dataItem)}
                className="k-grid-edit-command"
              >
                Edit
              </Button>
            </td>
          )}}
        />
      </Grid>
      {showDialog && (
        <Dialog 
          title={editingTeam.UserTeamID ? 'Edit Team' : 'Add New Team'}
          onClose={this.closeDialog}
          className='dialog-w800'
        >
          <div className="k-form k-form-md p-0">
            <FieldWrapper>
              <Input
                required
                label='Team Name'
                value={editingTeam.Name}
                onChange={(e) => this.handleInputChange('Name', e.value)}
                maxLength={60}
                placeholder="Enter team name"
                className="form-control"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Weekly Orders Booked Count Goal</Label>
              <NumericTextBox
                value={editingTeam.WeeklyBookedCount}
                min={1}
                max={1000}
                placeholder="Leave blank for no goal"
                onChange={(e) => this.handleInputChange('WeeklyBookedCount', e.value)}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Weekly Total Charges Goal</Label>
              <NumericTextBox
                value={editingTeam.WeeklyTotalCharges}
                min={1}
                max={500000}
                format="c2"
                placeholder="Leave blank for no goal"
                onChange={(e) => this.handleInputChange('WeeklyTotalCharges', e.value)}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Weekly Total Margin Goal</Label>
              <NumericTextBox
                value={editingTeam.WeeklyTotalMargin}
                min={1}
                max={500000}
                format="c2"
                placeholder="Leave blank for no goal"
                onChange={(e) => this.handleInputChange('WeeklyTotalMargin', e.value)}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Checkbox
                checked={editingTeam.Active}
                onChange={(e) => this.handleInputChange('Active', e.value)}
                label="Active"
              />
            </FieldWrapper>
          </div>
          <DialogActionsBar>
            <Button 
              onClick={this.closeDialog}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              themeColor="primary"
              onClick={this.saveTeam}
              disabled={saving || !editingTeam.Name.trim()}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  }
}